import React from 'react';
import { Image as ImageIcon, ExternalLink, Download, X, Table as TableIcon, PackageIcon } from 'lucide-react';
import { TableContent } from './TableContent';
import CheckpointContent from './CheckpointContent';

const ArtifactsPanel = ({ artifacts, onClose, hideHeader = false }) => {
  // URL validation to prevent XSS attacks
  const isValidUrl = (url) => {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  };

  const safeOpenUrl = (url) => {
    if (isValidUrl(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      console.error('Invalid URL blocked:', url);
    }
  };
  if (!artifacts || artifacts.length === 0) {
    return null;
  }

  return (
    <div className={hideHeader ? "h-full flex flex-col" : "w-1/2 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col"}>
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Artifacts ({artifacts.length})
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Artifacts List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {artifacts.map((artifact, index) => (
          <div
            key={artifact.id || index}
            className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 overflow-hidden"
          >
            {/* Artifact Header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center gap-2">
                {/* Icon based on artifact type */}
                {artifact.type === 'Checkpoint' ? (
                  <PackageIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                ) : artifact.type === 'Table' ? (
                  <TableIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                )}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {artifact.title || `Visualization ${index + 1}`}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {artifact.type || 'Chart'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => safeOpenUrl(artifact.url)}
                  className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Open in new tab"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = artifact.url;
                    link.download = artifact.title || 'visualization.png';
                    link.click();
                  }}
                  className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Artifact Content */}
            <div className="p-4">
              {artifact.type === 'Checkpoint' ? (
                // Render checkpoint data
                <CheckpointContent data={artifact.data} />
              ) : artifact.type === 'Table' ? (
                // Render table data
                <div>
                  <TableContent data={artifact.data} maxRows={100} />
                  {artifact.description && (
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                      {artifact.description}
                    </p>
                  )}
                </div>
              ) : (
                // Render visualizations (charts/images)
                <div className="relative group">
                  {/* Check if URL is HTML file (interactive Plotly) or image */}
                  {artifact.url && (() => {
                    try {
                      const url = new URL(artifact.url);
                      return url.pathname.endsWith('.html');
                    } catch {
                      return artifact.url.includes('.html');
                    }
                  })() ? (
                    // Interactive HTML visualization (Plotly)
                    <iframe
                      src={artifact.url}
                      title={artifact.title || 'Interactive Visualization'}
                      className="w-full rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                      style={{ height: '500px' }}
                      sandbox="allow-scripts allow-same-origin"
                      onError={(e) => {
                        console.error('Error loading HTML visualization:', e);
                      }}
                    />
                  ) : (
                    // Static image visualization (matplotlib/seaborn)
                    <>
                      <img
                        src={artifact.url}
                        alt={artifact.title || 'Generated Visualization'}
                        className="w-full h-auto rounded-lg cursor-pointer hover:opacity-95 transition-opacity shadow-sm"
                        onClick={() => safeOpenUrl(artifact.url)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div
                        className="hidden flex-col items-center justify-center p-8 text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600"
                      >
                        <ImageIcon className="h-8 w-8 mb-2 opacity-50" />
                        <p className="text-sm text-center">Unable to load visualization</p>
                        <button
                          onClick={() => safeOpenUrl(artifact.url)}
                          className="mt-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs underline"
                        >
                          Open in new tab
                        </button>
                      </div>
                    </>
                  )}

                  {artifact.description && (
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                      {artifact.description}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ArtifactsPanel;